import InventoriesApiV2 from 'api/v2/admins/inventories-api-v2'
import ProductsApiV2 from 'api/v2/admins/products-api-v2'
import TitlePage from 'components/atoms/title-page'
import StatusBadge from 'components/atoms/status-badge'
import { CardLoader } from 'components/organisms'
import { ALERT_TYPES } from 'constants/alert-constants'
import InventoryTypeBadge from 'domains/inventory/atoms/inventory-type-badge'
import ProductStatusBadge from 'domains/inventory/atoms/product-status-badge'
import InventoryMovementTimeline from 'domains/inventory/organisms/movement-timeline'
import BulkMarkSoldOnHoldModal from 'domains/inventory/organisms/bulk-mark-sold-on-hold-modal/bulk-mark-sold-on-hold-modal'
import SendPhotoLinkEmailModal from 'domains/inventory/organisms/send-photo-link-email-modal/send-photo-link-email-modal'
import OldDealActions, { canUseOldDeals } from 'domains/inventory/organisms/old-deal-actions'
import SwapBkrToBkaConfirmationModal from 'domains/product/organisms/swap-bkr-to-bka-confirmation-modal/swap-bkr-to-bka-confirmation-modal'
import SwapBkrToBkxConfirmationModal from 'domains/product/organisms/swap-bkr-to-bkx-confirmation-modal/swap-bkr-to-bkx-confirmation-modal'
import SwapBrvToBkvConfirmationModal from 'domains/product/organisms/swap-brv-to-bkv-confirmation-modal/swap-brv-to-bkv-confirmation-modal'
import inventoryMovementProcessMap from 'domains/inventory/pages/inventory-detail-page/inventory-movement-process-map'
import inventoryMovementProcessButtonLabelMap from 'domains/inventory/pages/inventory-detail-page/inventory-movement-process-button-label-map'
import { normalizeInventoryPricePermissions } from 'domains/inventory/utils/inventory-price-permission-util'
import { getInventoryShoesDetailData } from 'domains/inventory/utils/inventory-shoes-v2-helper'
import withLoader from 'hocs/with-loader'
import { useAuth } from 'providers/auth-provider'
import { useModal } from 'providers/modal-provider'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router'
import { Button, Card, CardBody, CardFooter, CardText, Col, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table } from 'reactstrap'
import PriceUtil from 'utils/price-util'

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const imagePreviewPropTypes = {
  title : PropTypes.string.isRequired,
  src   : PropTypes.string,
  alt   : PropTypes.string
}

const normalizeDisplayValue = (value) => {
  if (value === undefined || value === null || value === '') return '-'

  if (typeof value === 'object') {
    if (value.name) return value.name
    if (value.label) return value.label
    if (value.title) return value.title

    return '-'
  }

  return value
}

const firstPresent = (...values) => (
  values.find((value) => value !== undefined && value !== null && value !== '')
)

const uniquePresentValues = (values = []) => ([
  ...new Set(
    values
      .map((value) => normalizeDisplayValue(value))
      .filter((value) => value !== '-')
  )
])

const getDetailObject = (product) => {
  const details = product?.details

  if (!details || typeof details !== 'object') return {}

  return details
}

const getProductField = (product, fieldNames) => {
  const details = getDetailObject(product)
  const values  = fieldNames.flatMap((fieldName) => [
    product?.[fieldName],
    details?.[fieldName]
  ])

  return normalizeDisplayValue(firstPresent(...values))
}

const getMeasurementField = ({ product, productDimension, fieldNames }) => {
  const details      = getDetailObject(product)
  const measurements = productDimension?.measurements || {}
  const values       = fieldNames.flatMap((fieldName) => [
    measurements?.[fieldName],
    product?.[fieldName],
    details?.[fieldName]
  ])

  return normalizeDisplayValue(firstPresent(...values))
}

const formatPrice = (value) => {
  if (value === undefined || value === null || value === '') return '-'

  const numericValue = Number(value)

  if (!Number.isNaN(numericValue)) {
    return PriceUtil.format(Math.floor(numericValue).toString())
  }

  return normalizeDisplayValue(value)
}

const getInventoryTypeValue = (inventory = {}, product = {}) => (
  inventory.mapped_inventory_type ||
  product.mapped_inventory_type ||
  product.inventory_type ||
  inventory.inventory_type ||
  product.productable_type
)

const getInventoryTypeLabel = (inventory = {}, product = {}) => normalizeDisplayValue(firstPresent(
  inventory.mapped_inventory_type_label,
  product.mapped_inventory_type_label,
  inventory.inventory_type_label,
  product.inventory_type_label
))

const InventoryTypeDisplay = ({ inventoryType, inventoryTypeLabel }) => {
  if (inventoryTypeLabel !== '-') {
    return <StatusBadge color='primary' label={ inventoryTypeLabel } />
  }

  if (!inventoryType) return '-'

  return <InventoryTypeBadge inventory_type={ inventoryType } />
}

InventoryTypeDisplay.propTypes = {
  inventoryType      : PropTypes.string,
  inventoryTypeLabel : PropTypes.string
}

const getSubLocationLabel = (inventoryLocations = []) => {
  const subLocations = uniquePresentValues(
    inventoryLocations.flatMap((location) => [
      location?.sublocation,
      location?.name,
      location?.rack_name,
      location?.rack,
      location?.sub_location
    ])
  )

  return subLocations.length ? subLocations.join(', ') : '-'
}

const getPhotoUrl = (photo) => {
  if (!photo) return null
  if (typeof photo === 'string') return photo

  return photo.url || photo.src || photo.image_url || null
}

const firstPhotoUrl = (photos = []) => {
  if (!Array.isArray(photos)) return null

  return getPhotoUrl(photos.find((photo) => getPhotoUrl(photo)))
}

const EDIT_SUB_LOCATION_ALLOWED_EMAILS = [
  'sweta.sabilillah.belleandkate@gmail.com'
]

const EDIT_SUB_LOCATION_ALLOWED_ROLES = [
  'superadmin',
  'owner'
]

const APPRAISAL_NOTE_ALLOWED_EMAILS = [
  'angel@belleandkate.com',
  'mifta.belleandkate@gmail.com',
  'jac@belleandkate.com'
]

const APPRAISAL_NOTE_ALLOWED_ROLES = [
  'purchasing',
  'product curated',
  'owner',
  'admin'
]

const APPRAISAL_STATE_LABELS = {
  need_appraisal      : 'Need Appraisal',
  need_re_appraisal   : 'Need Re-Appraisal',
  need_confirm_hm     : 'Confirm Buying Price',
  need_approval       : 'Need Approval',
  appraisal_approved  : 'Appraisal Approved',
  appraisal_rejected  : 'Appraisal Rejected'
}

const normalizePermissionValue = (value = '') => value.toString().trim().toLowerCase().replace(/_/g, ' ')

const canEditSubLocation = (user = {}) => {
  const role  = normalizePermissionValue(user?.role || user?.access || '')
  const email = normalizePermissionValue(user?.email || '')

  return EDIT_SUB_LOCATION_ALLOWED_ROLES.includes(role) || EDIT_SUB_LOCATION_ALLOWED_EMAILS.includes(email)
}

const canViewAppraisalNote = (user = {}) => {
  const role  = normalizePermissionValue(user?.role || user?.access || '')
  const email = normalizePermissionValue(user?.email || '')

  return APPRAISAL_NOTE_ALLOWED_ROLES.includes(role) || APPRAISAL_NOTE_ALLOWED_EMAILS.includes(email)
}

const appraisalStateLabel = (product = {}) => (
  normalizeDisplayValue(product.appraisal_state_label) !== '-'
    ? normalizeDisplayValue(product.appraisal_state_label)
    : APPRAISAL_STATE_LABELS[product.appraisal_state] || normalizeDisplayValue(product.appraisal_state)
)

const ImagePreview = ({ title, src, alt }) => (
  <div className='mb-3'>
    <p className='small text-uppercase text-muted font-weight-bold mb-2'>{ title }</p>
    {
      src ? (
        <a href={ src } target='_blank' rel='noopener noreferrer' className='d-block text-decoration-none'>
          <div
            className='overflow-hidden rounded d-flex align-items-center justify-content-center border bg-light'
            style={{
              width  : '100%',
              height : '220px'
            }}
          >
            <img
              src={ src }
              alt={ alt || title }
              loading='lazy'
              decoding='async'
              style={{
                width     : '100%',
                height    : '100%',
                objectFit : 'contain'
              }}
            />
          </div>
        </a>
      ) : (
        <div
          className='rounded d-flex align-items-center justify-content-center border bg-light text-muted'
          style={{
            width  : '100%',
            height : '220px'
          }}
        >
          { '-' }
        </div>
      )
    }
  </div>
)

ImagePreview.propTypes = imagePreviewPropTypes

const InventoryDetailPage = ({ pageUtils }) => {
  const [ inventory, setInventory ]                                 = useState({})
  const [ reinitializePage, setReinitializePage ]                   = useState(true)
  const [ isLoading, setIsLoading ]                                 = useState(true)
  const [ isQuantityGuideOpen, setIsQuantityGuideOpen ]             = useState(false)
  const [ isInventoryMovementOpen, setIsInventoryMovementOpen ]     = useState(false)
  const [ isSwappingBkrToBka, setIsSwappingBkrToBka ]               = useState(false)
  const [ isSwappingBkrToBkx, setIsSwappingBkrToBkx ]               = useState(false)
  const [ isSwappingBrvToBkv, setIsSwappingBrvToBkv ]               = useState(false)
  const [ isMarkStatusOpen, setIsMarkStatusOpen ]                     = useState(false)
  const [ isBulkMarkStatusOpen, setIsBulkMarkStatusOpen ]              = useState(false)
  const [ markStatusAction, setMarkStatusAction ]                      = useState('sold')
  const [ isMarkingStatus, setIsMarkingStatus ]                        = useState(false)
  const [ sqAdjustment, setSqAdjustment ] = useState(null)
  const [ sqReason, setSqReason ] = useState('')
  const [ isAdjustingSq, setIsAdjustingSq ] = useState(false)
  const [ sqError, setSqError ] = useState('')
  const [ cancelSkuOpen, setCancelSkuOpen ] = useState(false)
  const [ cancelSkuNote, setCancelSkuNote ] = useState('')
  const [ physicalAbsent, setPhysicalAbsent ] = useState(false)
  const [ cancelSkuError, setCancelSkuError ] = useState('')
  const [ cancellingSku, setCancellingSku ] = useState(false)
  const [ isPhotoLinkEmailOpen, setIsPhotoLinkEmailOpen ] = useState(false)

  const { user } = useAuth()
  const canUseOldDeal = canUseOldDeals(user)
  const { showModal, hideModal } = useModal()

  const handleCancelSku = async () => {
    if (cancellingSku || !physicalAbsent || !cancelSkuNote.trim()) return
    setCancellingSku(true)
    setCancelSkuError('')
    try {
      const response = await InventoriesApiV2.cancelConsign(inventory.id, cancelSkuNote.trim(), physicalAbsent)
      setCancelSkuOpen(false)
      pageUtils.setAlertMsg(response.data.message, ALERT_TYPES.SUCCESS)
      setReinitializePage(true)
    } catch (error) {
      setCancelSkuError(error.response?.data?.message || 'Cancel Consignment gagal. Refresh data untuk memeriksa blocker terbaru.')
    } finally {
      setCancellingSku(false)
    }
  }

  const handleAdjustSq = async () => {
    if (isAdjustingSq || !sqAdjustment || inventory.permissions?.can_adjust_sellable_quantity !== true) return
    if (!sqReason.trim()) {
      setSqError('Reason wajib diisi.')
      return
    }
    setIsAdjustingSq(true)
    setSqError('')
    try {
      const response = await InventoriesApiV2.adjustSellableQuantity({ inventory_id: inventory.id, action: sqAdjustment, reason: sqReason.trim() })
      setInventory(response.data.inventory)
      setSqAdjustment(null)
      setIsInventoryMovementOpen(true)
    }
    catch (error) {
      setSqError(error.response?.data?.messages?.base || error.response?.data?.message || 'Gagal memperbarui SQ.')
    }
    finally {
      setIsAdjustingSq(false)
    }
  }

  const { id } = useParams()

  const history = useHistory()

  useEffect(() => {
    if (reinitializePage) {
      InventoriesApiV2.show(id)
        .then((response) => {
          setInventory(response.data.inventory)
          setIsLoading(false)
        })
        .catch(() => {
          pageUtils.setAlertMsg('Unable to fetch inventory detail', ALERT_TYPES.DANGER)
        })
      // * to make sure this block won't execute many times
      setReinitializePage(false)
    }

  }, [ id, pageUtils, reinitializePage ])

  const handleProcess = useCallback((data) => {
    const process_id   = data.process_id
    const process_type = data.process_type
    const action       = data.action
    const pathname     = inventoryMovementProcessMap(
      process_id,
      process_type,
      action,
      data
    )

    if (!pathname) return

    history.push({ pathname })
  }, [ history ])


  const generateButtonLabel = (data) => {
    const process_id   = data.process_id
    const process_type = data.process_type

    return inventoryMovementProcessButtonLabelMap(process_id, process_type, data)
  }

  const handleOpenEditInventoryPage = () => {

    history.push({
      pathname: `/app/warehouse-team/inventory/${id}/edit-page`
    })
  }

  const toggleQuantityGuide = () => {
    setIsQuantityGuideOpen((currentValue) => !currentValue)
  }

  const toggleInventoryMovement = () => {
    setIsInventoryMovementOpen((currentValue) => !currentValue)
  }


  const submitMarkSoldOnHold = async () => {
    if (isMarkingStatus) return
    setIsMarkingStatus(true)

    try {
      const response = await InventoriesApiV2.markSoldOnHold({
        inventory_id : id,
        action       : markStatusAction
      })
      pageUtils.setAlertMsg(response.data?.message || 'Inventory status updated.', ALERT_TYPES.SUCCESS)
      setIsMarkStatusOpen(false)
      setReinitializePage(true)
    } catch (error) {
      pageUtils.setApiErrorMsg(error.response?.data || { messages: { base: error.message } })
    } finally {
      setIsMarkingStatus(false)
    }
  }

  const inventoryProductIdentifier = ({ inventoryData = {}, productData = {} } = {}) => (
    productData.id ||
    inventoryData.product_id ||
    productData.sku ||
    inventoryData.product_sku ||
    inventoryData.inventory_sku
  )

  const executeSwapBkrToBka = async (swapReason) => {
    const currentProduct = inventory.product || {}
    const productIdentifier = inventoryProductIdentifier({ inventoryData: inventory, productData: currentProduct })

    if (!productIdentifier) {
      pageUtils.setApiErrorMsg({ messages: { base: 'Product identifier tidak ditemukan untuk inventory ini.' } })
      hideModal()
      return
    }

    setIsSwappingBkrToBka(true)

    await ProductsApiV2.swapBkrToBka({ id: productIdentifier, swapReason })
      .then((response) => {
        const newInventoryId = response.data?.new_inventory_id

        pageUtils.setAlertMsg(response.data?.message || 'Product swapped to BKA.', ALERT_TYPES.SUCCESS)

        if (newInventoryId) {
          history.push({ pathname: `/app/warehouse-team/inventory/${newInventoryId}` })
          setReinitializePage(true)
          return
        }

        setReinitializePage(true)
      })
      .catch((error) => {
        pageUtils.setApiErrorMsg(error.response?.data || { messages: { base: error.message } })
      })
      .finally(() => {
        setIsSwappingBkrToBka(false)
        hideModal()
      })
  }

  const handleSwapBkrToBka = () => {
    const currentProduct = inventory.product || {}

    showModal(SwapBkrToBkaConfirmationModal, {
      sku                : currentProduct.sku || inventory.inventory_sku || 'this product',
      handleConfirmation : executeSwapBkrToBka
    })
  }

  const executeSwapBkrToBkx = async (deletionNote) => {
    const currentProduct = inventory.product || {}
    const productIdentifier = inventoryProductIdentifier({ inventoryData: inventory, productData: currentProduct })

    if (!productIdentifier) {
      pageUtils.setApiErrorMsg({ messages: { base: 'Product identifier tidak ditemukan untuk inventory ini.' } })
      hideModal()
      return
    }

    setIsSwappingBkrToBkx(true)

    await ProductsApiV2.swapBkrToBkx({ id: productIdentifier, deletionNote })
      .then((response) => {
        pageUtils.setAlertMsg(response.data?.message || 'Product marked as BKX.', ALERT_TYPES.SUCCESS)
        setReinitializePage(true)
      })
      .catch((error) => {
        pageUtils.setApiErrorMsg(error.response?.data || { messages: { base: error.message } })
      })
      .finally(() => {
        setIsSwappingBkrToBkx(false)
        hideModal()
      })
  }

  const handleSwapBkrToBkx = () => {
    const currentProduct = inventory.product || {}

    showModal(SwapBkrToBkxConfirmationModal, {
      sku                : currentProduct.sku || inventory.inventory_sku || 'this product',
      handleConfirmation : executeSwapBkrToBkx
    })
  }

  const executeSwapBrvToBkv = async (swapReason) => {
    const currentProduct = inventory.product || {}
    const productIdentifier = inventoryProductIdentifier({ inventoryData: inventory, productData: currentProduct })

    if (!productIdentifier) {
      pageUtils.setApiErrorMsg({ messages: { base: 'Product identifier tidak ditemukan untuk inventory ini.' } })
      hideModal()
      return
    }

    setIsSwappingBrvToBkv(true)

    await ProductsApiV2.swapBrvToBkv({ id: productIdentifier, swapReason })
      .then((response) => {
        const newInventoryId = response.data?.new_inventory_id

        pageUtils.setAlertMsg(response.data?.message || 'Product swapped to BKV.', ALERT_TYPES.SUCCESS)

        if (newInventoryId) {
          history.push({ pathname: `/app/warehouse-team/inventory/${newInventoryId}` })
          setReinitializePage(true)
          return
        }

        setReinitializePage(true)
      })
      .catch((error) => {
        pageUtils.setApiErrorMsg(error.response?.data || { messages: { base: error.message } })
      })
      .finally(() => {
        setIsSwappingBrvToBkv(false)
        hideModal()
      })
  }

  const handleSwapBrvToBkv = () => {
    const currentProduct = inventory.product || {}

    showModal(SwapBrvToBkvConfirmationModal, {
      sku                : currentProduct.sku || inventory.inventory_sku || 'this product',
      handleConfirmation : executeSwapBrvToBkv
    })
  }

  /** Inventory Movement Timeline with card loader */
  const InventoryMovementTimelineWithLoader = withLoader(() => (
    <InventoryMovementTimeline
      inventoryMovements={ inventory.movements }
      onProcessButtonClick={ handleProcess }
      buttonLabel = { generateButtonLabel }
    />
  ), CardLoader)

  const product                 = inventory.product || {}
  const isBkc = String(inventory.product_group_code || product.group?.code || product.sku?.split('-')[0] || '').trim().toUpperCase() === 'BKC'
  const productDimension        = inventory.product_dimension || {}
  const shoesDetail             = getInventoryShoesDetailData(product)
  const productPhotos           = Array.isArray(product.photos) ? product.photos : []
  const productImage            = firstPresent(
    product.main_image,
    product.product_photo_url,
    firstPresent(...(product.product_photo_urls || [])),
    firstPhotoUrl(productPhotos)
  )
  const completenessImage       = firstPresent(
    product.comes_with_image_url,
    product.completeness_photo_url,
    firstPresent(...(product.completeness_photo_urls || []))
  )
  const productPhotoFolderLink  = normalizeDisplayValue(firstPresent(
    product.photo_folder_link,
    product.photoFolderLink,
    product.photo_folder_url,
    product.photoFolderUrl,
    product.google_drive_link,
    product.googleDriveLink,
    product.drive_link,
    product.driveLink,
    getProductField(product, [
      'photo_folder_link',
      'photoFolderLink',
      'photo_folder_url',
      'photoFolderUrl',
      'google_drive_link',
      'googleDriveLink',
      'drive_link',
      'driveLink'
    ])
  ))
  const authenticationStatusLabel = normalizeDisplayValue(firstPresent(product.authentication_summary_label, product.authentication_status_label))
  const authenticationCertificateLink = normalizeDisplayValue(firstPresent(product.authentication_certificate_url, product.authentication_certificate_link))
  const productStatus           = product.status || product.product_status
  const markSoldOnHoldStatus    = firstPresent(inventory.mark_sold_on_hold_status, product.mark_sold_on_hold_status)
  const consignorReturnStatus   = firstPresent(inventory.consignor_return_status, product.consignor_return_status)
  const productStatusBadgeProps = {
    className : 'badge-lg text-wrap d-inline-block',
    style     : { minWidth: '82px', textAlign: 'center' }
  }
  const appraisalState          = appraisalStateLabel(product)
  const canShowAppraisalNote    = inventory.permissions?.can_view_appraisal_note !== undefined
    ? inventory.permissions.can_view_appraisal_note === true
    : canViewAppraisalNote(user)
  const productSize             = firstPresent(
    product.product_size,
    productDimension.size,
    getProductField(product, [ 'product_size', 'size', 'p_size' ])
  )
  const inventoryType           = getInventoryTypeValue(inventory, product)
  const inventoryTypeLabel      = getInventoryTypeLabel(inventory, product)
  const sellerName              = normalizeDisplayValue(firstPresent(
    product.consignor_seller_name,
    product.seller_name,
    product.consignor_name,
    product.supplier_name
  ))
  const sellerId                = firstPresent(
    product.consignor_user_id,
    product.seller_id,
    product.consignor_id,
    inventory.consignor_user_id,
    inventory.seller_id,
    inventory.consignor_id
  )

  const itemLocationLabel = normalizeDisplayValue(firstPresent(
    product.item_location_label,
    product.item_location_name,
    product.location_item_name,
    product.product_location_name,
    product.item_location?.name
  ))

  const subLocationLabel = normalizeDisplayValue(firstPresent(
    product.box_location_label,
    product.box_location_name,
    product.box_location?.name,
    product.rack_label,
    product.sub_location_label
  ))

  const inclusionsLocationLabel = normalizeDisplayValue(firstPresent(
    product.inclusions_location_label,
    product.inclusions_location_name,
    product.inclusions_location?.name
  ))
  const permissions             = inventory.permissions || {}
  const canAdjustSellableQuantity = permissions.can_adjust_sellable_quantity === true
  const pricePermissions        = normalizeInventoryPricePermissions(permissions)
  const canShowPriceInformation = pricePermissions.canViewBuyingPrice ||
    pricePermissions.canViewDefaultPrice ||
    pricePermissions.canViewSellingPrice
  const canManageSubLocation    = permissions.can_update_location !== undefined ? permissions.can_update_location : canEditSubLocation(user)
  const canOpenEditInventory    = permissions.can_open_edit_inventory !== undefined ? permissions.can_open_edit_inventory : (canManageSubLocation || !!user)
  const sellableQuantityLabel   = normalizeDisplayValue(inventory.sellable_quantity)
  const inventoryQuantityLabel  = normalizeDisplayValue(inventory.stored_quantity)
  const showUnavailableLabel    = inventory.show_unavailable_product ? 'Yes' : 'No'
  const productStatusNote       = normalizeDisplayValue(firstPresent(
    product.product_status_note,
    product.status_note,
    product.deletion_note,
    inventory.product_status_note,
    inventory.status_note,
    inventory.deletion_note
  ))
  const customerFacingInventoryId = firstPresent(
    inventory.id,
    inventory.inventory_id,
    product.inventory_id
  )
  const customerFacingProductUrl = customerFacingInventoryId ?
    `https://belleandkate.com/products/${ customerFacingInventoryId }` :
    null
  const canSwapBkrToBka         = inventory.can_swap_bkr_to_bka === true && permissions.can_swap_bkr_to_bka === true
  const canSwapBkrToBkx         = inventory.can_swap_bkr_to_bkx === true && permissions.can_swap_bkr_to_bkx === true
  const canSwapBrvToBkv         = inventory.can_swap_brv_to_bkv === true && permissions.can_swap_brv_to_bkv === true
  const canMarkSoldOnHold        = permissions.can_mark_sold_on_hold === true
  const canSendPhotoLinkEmail    = product.can_send_photo_link_email === true

  return (
    <>
      <TitlePage mainTitle={ 'Inventory' } />
      <Card className='mb-3'>
        <CardBody className='py-3'>
          <div className='d-flex flex-column flex-md-row align-items-md-center justify-content-between'>
            <div>
              <h4 className='mb-1'>{ 'Panduan Quantity Phase 2' }</h4>
              <p className='mb-0 text-muted'>
                { 'Panduan singkat SQ, IQ, dan rule movement inventory. Klik tombol di kanan untuk membaca detail.' }
              </p>
            </div>
            <Button
              color='primary'
              outline
              size='sm'
              className='mt-3 mt-md-0'
              onClick={ toggleQuantityGuide }
            >
              { isQuantityGuideOpen ? 'Sembunyikan Panduan' : 'Tampilkan Panduan' }
            </Button>
          </div>
          {
            isQuantityGuideOpen ?
              (
                <>
                  <hr />
                  <p className='text-muted'>
                    { 'Sebuah produk dapat memiliki lebih dari 1 ukuran. Gudang dapat menyimpan produk ini di lokasi yang berbeda. Setiap produk perlu diidentifikasi secara unik oleh sistem. "Inventory" mengacu pada produk unik dengan ukuran yang unik.' }
                  </p>
                  <Row>
                    <Col xs={ 12 }>
                      <h4>{ 'Quantity Phase 2' }</h4>
                      <p className='mb-1'>
                        { 'Sellable Qty (SQ): jumlah stok yang dapat dijual ke customer. Produk dapat dijual selama Sellable Qty minimal 1.' }
                      </p>
                      <p className='mb-0'>
                        { 'Inventory Qty (IQ): jumlah stok fisik yang masih tersedia di warehouse/lokasi penyimpanan.' }
                      </p>
                    </Col>
                  </Row>
                </>
              ) : null
          }
        </CardBody>
      </Card>
      <Row className='align-items-start'>
        <Col xs={ 12 } xl={ 7 }>
          <Card>
            <div className='p-3'>
              <h4>{ 'Product' }</h4>
                <CardText>{ 'Data produk dan rak yang terhubung dengan inventory ini.' }</CardText>
            </div>
            <CardBody className='pt-0'>
              <Row>
                <Col xs={ 12 } md={ 6 } lg={ 12 } xl={ 6 }>
                  <ImagePreview
                    title='Foto Product'
                    src={ productImage }
                    alt={ product.name || inventory.inventory_sku || 'Product' }
                  />
                </Col>
                <Col xs={ 12 } md={ 6 } lg={ 12 } xl={ 6 }>
                  <ImagePreview
                    title='Foto Kelengkapan'
                    src={ completenessImage }
                    alt={ `Foto Kelengkapan ${product.name || inventory.inventory_sku || ''}` }
                  />
                </Col>

                {
                  shoesDetail.isShoes && shoesDetail.insolePhotoUrl ? (
                    <Col xs={ 12 } md={ 6 } lg={ 12 } xl={ 6 }>
                      <ImagePreview
                        title='Insole Photo'
                        src={ shoesDetail.insolePhotoUrl }
                        alt={ `Insole Photo ${product.name || inventory.inventory_sku || ''}` }
                      />
                    </Col>
                  ) : null
                }
              </Row>
            </CardBody>
            <CardBody className='pt-0'>
              <h5 className='mb-3'>{ 'Informasi Quantity' }</h5>
              <Table size='sm' responsive className='mb-0'>
                <tbody>
                  <tr>
                    <td>{ 'Sellable Quantity (SQ)' }</td>
                    <td className='font-weight-bold'>{ sellableQuantityLabel }</td>
                  </tr>
                  <tr>
                    <td>{ 'Inventory Quantity (IQ)' }</td>
                    <td className='font-weight-bold'>{ inventoryQuantityLabel }</td>
                  </tr>
                  <tr>
                    <td>{ 'Tampilkan sebagai tidak tersedia di website/app' }</td>
                    <td>{ showUnavailableLabel }</td>
                  </tr>
                </tbody>
              </Table>
              { inventory.cancel_consign && (
                <div className='mt-3'>
                  <Button color='danger' disabled={ !inventory.cancel_consign.allowed } onClick={ () => { setCancelSkuError(''); setCancelSkuOpen(true) } }>Cancel Consignment</Button>
                  { inventory.cancel_consign.blockers?.length > 0 && (
                    <div className='text-danger mt-2'>
                      <strong>Cancel Consignment diblokir:</strong>
                      <ul>{ inventory.cancel_consign.blockers.map((reason) => <li key={ reason }>{ reason }</li>) }</ul>
                    </div>
                  ) }
                  <Modal isOpen={ cancelSkuOpen } toggle={ () => !cancellingSku && setCancelSkuOpen(false) }>
                    <ModalHeader>Cancel Consignment { product.sku }</ModalHeader>
                    <ModalBody>
                      <p>Gunakan hanya untuk input consign batal/duplikat yang barang fisiknya tidak ada. Produk akan dihapus secara soft-delete; IQ dan SQ menjadi 0.</p>
                      <Label>Alasan / tiket dan nama verifikator fisik</Label>
                      <Input type='textarea' value={ cancelSkuNote } onChange={ (event) => setCancelSkuNote(event.target.value) } disabled={ cancellingSku } />
                      <FormGroup check className='mt-3'>
                        <Label check><Input type='checkbox' checked={ physicalAbsent } onChange={ (event) => setPhysicalAbsent(event.target.checked) } disabled={ cancellingSku } />Saya memastikan barang fisik tidak ada. Bila harus mengembalikan barang, gunakan Consignor Return.</Label>
                      </FormGroup>
                      { cancelSkuError && <div role='alert' className='text-danger mt-3' style={{ whiteSpace: 'pre-line' }}>{ cancelSkuError }</div> }
                    </ModalBody>
                    <ModalFooter>
                      <Button color='secondary' disabled={ cancellingSku } onClick={ () => setCancelSkuOpen(false) }>Kembali</Button>
                      <Button color='danger' disabled={ cancellingSku || !physicalAbsent || !cancelSkuNote.trim() } onClick={ handleCancelSku }>{ cancellingSku ? 'Memproses...' : 'Konfirmasi Cancel Consignment' }</Button>
                    </ModalFooter>
                  </Modal>
                </div>
              ) }

            </CardBody>
            {
              canShowPriceInformation ? (
                <CardBody className='pt-0'>
                  <h5 className='mb-3'>{ 'Informasi Harga' }</h5>
                  <Table size='sm' responsive className='mb-0'>
                    <tbody>
                      {
                        pricePermissions.canViewBuyingPrice ? (
                          <tr>
                            <td>{ 'Buying Price' }</td>
                            <td>{ formatPrice(product.buying_price) }</td>
                          </tr>
                        ) : null
                      }
                      {
                        pricePermissions.canViewDefaultPrice ? (
                          <tr>
                            <td>{ 'Default Price' }</td>
                            <td>{ formatPrice(product.default_price) }</td>
                          </tr>
                        ) : null
                      }
                      {
                        pricePermissions.canViewSellingPrice ? (
                          <tr>
                            <td>{ 'Selling Price' }</td>
                            <td>{ formatPrice(product.selling_price) }</td>
                          </tr>
                        ) : null
                      }
                    </tbody>
                  </Table>
                </CardBody>
              ) : null
            }
            <CardBody className='pt-0'>
              <h5 className='mb-3'>{ 'Product Detail' }</h5>
              <Table size='sm' responsive className='mb-0'>
                <tbody>
                <tr>
                  <td>{ 'SKU' }</td>
                  <td>{ normalizeDisplayValue(product.sku || inventory.inventory_sku) }</td>
                </tr>
                <tr>
                  <td>{ 'Condition' }</td>
                  <td>{ getProductField(product, [ 'condition_name', 'condition', 'condition_label' ]) }</td>
                </tr>
                <tr>
                  <td>{ 'Product Name' }</td>
                  <td className='text-wrap'>{ normalizeDisplayValue(product.name) }</td>
                </tr>
                <tr>
                  <td>{ 'Stamp' }</td>
                  <td className='text-wrap'>{ getProductField(product, [ 'stamp' ]) }</td>
                </tr>
                <tr>
                  <td>{ 'Defects' }</td>
                  <td className='text-wrap'>{ getProductField(product, [ 'defects' ]) }</td>
                </tr>
                <tr>
                  <td>{ 'Comes With' }</td>
                  <td className='text-wrap'>{ getProductField(product, [ 'comes_with', 'completeness', 'kelengkapan' ]) }</td>
                </tr>
                {
                  pricePermissions.canViewSupplierName ? (
                    <tr>
                      <td>{ 'Consignor/Seller Name' }</td>
                      <td className='text-wrap'>
                        {
                          sellerId && sellerName !== '-' ? (
                            <a href={ `/app/sales/users/${sellerId}` } className='font-weight-bold' target='_blank' rel='noopener noreferrer'>
                              { sellerName } #{ sellerId }
                            </a>
                          ) : sellerName
                        }
                      </td>
                    </tr>
                  ) : null
                }
                <tr>
                  <td>{ 'Product Curator' }</td>
                  <td>{ normalizeDisplayValue(product.product_curator_pic) }</td>
                </tr>
                <tr>
                  <td>{ 'Appraisal State' }</td>
                  <td>
                    {
                      appraisalState !== '-' ? (
                        <div>
                          <StatusBadge color='primary' label={ appraisalState } />
                          {
                            canShowAppraisalNote && normalizeDisplayValue(product.appraisal_note) !== '-' ? (
                              <div className='small text-muted mt-1' style={{ whiteSpace: 'pre-line' }}>
                                <span className='font-weight-bold'>{ 'Appraisal Note: ' }</span>
                                { normalizeDisplayValue(product.appraisal_note) }
                              </div>
                            ) : null
                          }
                        </div>
                      ) : '-'
                    }
                  </td>
                </tr>
                <tr>
                  <td>{ 'Inventory Type' }</td>
                  <td>
                    <InventoryTypeDisplay
                      inventoryType={ inventoryType }
                      inventoryTypeLabel={ inventoryTypeLabel }
                    />
                  </td>
                </tr>
                <tr>
                  <td>{ 'Product Status' }</td>
                  <td>
                    {
                      productStatus ?
                        <div>
                          <ProductStatusBadge product_status={ productStatus } { ...productStatusBadgeProps } />
                          { markSoldOnHoldStatus === 'sold' && (
                            <div className='mt-1'>
                              <StatusBadge color='danger' label='SOLD' { ...productStatusBadgeProps } />
                            </div>
                          ) }
                          { markSoldOnHoldStatus === 'on_hold' && (
                            <div className='mt-1'>
                              <StatusBadge color='warning' label='ON HOLD' { ...productStatusBadgeProps } />
                            </div>
                          ) }
                          { markSoldOnHoldStatus !== 'sold' && consignorReturnStatus === 'returned' && (
                            <div className='mt-1'>
                              <StatusBadge color='info' label='RETURNED' { ...productStatusBadgeProps } />
                            </div>
                          ) }
                          {
                            productStatusNote !== '-' ? (
                              <div className='small text-muted mt-1' style={{ whiteSpace: 'pre-line' }}>
                                <span className='font-weight-bold'>{ 'Note: ' }</span>
                                { productStatusNote }
                              </div>
                            ) : null
                          }
                        </div> :
                        '-'
                    }
                  </td>
                </tr>
                <tr>
                  <td>{ 'Customer Web Product' }</td>
                  <td>
                    {
                      customerFacingProductUrl ? (
                        <div>
                          <a
                            href={ customerFacingProductUrl }
                            target='_blank'
                            rel='noopener noreferrer'
                            className='font-weight-bold d-inline-block'
                          >
                            { 'Open product on customer web' }
                          </a>
                          <div className='small text-muted text-break mt-1'>
                            { customerFacingProductUrl }
                          </div>
                        </div>
                      ) : '-'
                    }
                  </td>
                </tr>
                <tr>
                  <td>{ 'Product Size' }</td>
                  <td>{ normalizeDisplayValue(productSize) }</td>
                </tr>
                {
                  shoesDetail.isShoes ? (
                    <tr>
                      <td>{ 'Insole Size' }</td>
                      <td>{ normalizeDisplayValue(shoesDetail.insoleSize) }</td>
                    </tr>
                  ) : null
                }
                <tr>
                  <td>{ 'Height' }</td>
                  <td>{ getMeasurementField({ product, productDimension, fieldNames: [ 'height' ] }) }</td>
                </tr>
                <tr>
                  <td>{ 'Length' }</td>
                  <td>{ getMeasurementField({ product, productDimension, fieldNames: [ 'length', 'lenght' ] }) }</td>
                </tr>
                <tr>
                  <td>{ 'Width' }</td>
                  <td>{ getMeasurementField({ product, productDimension, fieldNames: [ 'width' ] }) }</td>
                </tr>
                <tr>
                  <td>{ 'Strap Drop' }</td>
                  <td>{ getMeasurementField({ product, productDimension, fieldNames: [ 'strap_drop', 'strap drop' ] }) }</td>
                </tr>
                <tr>
                  <td>{ 'Handle Drop' }</td>
                  <td>{ getMeasurementField({ product, productDimension, fieldNames: [ 'handle_drop', 'handle drop' ] }) }</td>
                </tr>
                <tr>
                  <td colSpan={ 2 } className='py-3'>
                    <div>
                      <p className='font-weight-bold mb-2'>
                        { 'Link Foto Produk (Google Drive)' }
                      </p>
                      <input
                        type='text'
                        className='form-control'
                        value={ productPhotoFolderLink === '-' ? '' : productPhotoFolderLink }
                        placeholder='-'
                        readOnly
                        onClick={ (event) => event.target.select() }
                      />
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>{ 'Authentication Status' }</td>
                  <td className='text-wrap font-weight-bold'>{ authenticationStatusLabel }</td>
                </tr>
                <tr>
                  <td>{ 'Authentication Certificate / Link' }</td>
                  <td className='text-wrap'>
                    {
                      authenticationCertificateLink !== '-' ? (
                        <a href={ authenticationCertificateLink } target='_blank' rel='noopener noreferrer'>
                          { authenticationCertificateLink }
                        </a>
                      ) : '-'
                    }
                  </td>
                </tr>
                <tr>
                  <td>{ 'Authentication Notes' }</td>
                  <td className='text-wrap'>{ normalizeDisplayValue(product.authentication_notes) }</td>
                </tr>
                <tr>
                  <td>{ 'Lokasi Product / Item Location' }</td>
                  <td className='text-wrap font-weight-bold'>{ itemLocationLabel }</td>
                </tr>
                <tr>
                  <td>{ 'Sub-Location / Rak' }</td>
                  <td className='text-wrap font-weight-bold'>{ subLocationLabel }</td>
                </tr>
                <tr>
                  <td>{ 'Lokasi Kelengkapan' }</td>
                  <td className='text-wrap font-weight-bold'>{ inclusionsLocationLabel }</td>
                </tr>
                </tbody>
              </Table>
            </CardBody>
            {
              canUseOldDeal || canAdjustSellableQuantity || canOpenEditInventory || canSwapBkrToBka || canSwapBkrToBkx || canSwapBrvToBkv || canMarkSoldOnHold || canSendPhotoLinkEmail ? (
                <CardFooter className='d-flex flex-column'>
                  <small className='text-muted mb-2'>
                    {
                      canSwapBkrToBka || canSwapBkrToBkx || canSwapBrvToBkv || canMarkSoldOnHold ?
                        'Swap / exception actions hanya muncul untuk product dan user/role yang diizinkan.' :
                        'Edit Inventory dapat dibuka; field yang bisa diubah mengikuti akses masing-masing user.'
                    }
                  </small>
                  <div className='d-flex flex-column flex-sm-row flex-wrap align-items-stretch align-items-sm-center text-nowrap' style={{ gap: '0.5rem' }}>
                    {
                      canMarkSoldOnHold ? (
                        <Button
                          color='info'
                          className='mb-2 mb-sm-0 mr-sm-2'
                          onClick={ () => {
                            setMarkStatusAction(inventory.sellable_quantity > 0 ? 'on_hold' : 'sold')
                            setIsMarkStatusOpen(true)
                          } }
                        >
                          { 'Mark Sold / On Hold' }
                        </Button>
                      ) : null
                    }
                    {
                      canMarkSoldOnHold ? (
                        <Button
                          color='info'
                          outline
                          className='mb-2 mb-sm-0 mr-sm-2'
                          onClick={ () => setIsBulkMarkStatusOpen(true) }
                        >
                          { 'Bulk Sold / Hold' }
                        </Button>
                      ) : null
                    }
                    {
                      canSwapBkrToBka ? (
                        <Button
                          color='success'
                          className='mb-2 mb-sm-0 mr-sm-2'
                          onClick={ handleSwapBkrToBka }
                          disabled={ isSwappingBkrToBka }
                        >
                          { isSwappingBkrToBka ? 'Swapping...' : 'Swap BKR to BKA' }
                        </Button>
                      ) : null
                    }
                    { canSendPhotoLinkEmail && (
                      <Button
                        color='warning'
                        outline
                        className='mb-2 mb-sm-0 mr-sm-2'
                        onClick={ () => setIsPhotoLinkEmailOpen(true) }
                      >
                        <i className='fas fa-camera mr-2' />Input Photo Link and Send Email
                      </Button>
                    ) }
                    { canUseOldDeal && <OldDealActions inventoryId={ inventory.id } sku={ product.sku || inventory.inventory_sku || '' } onCreated={ () => { setReinitializePage(true); setIsInventoryMovementOpen(true) } } /> }
                    {
                      canOpenEditInventory ? (
                        <Button
                          color='primary'
                          onClick={ handleOpenEditInventoryPage }
                        >
                          { 'Edit Inventory' }
                        </Button>
                      ) : null
                    }
                    {
                      canSwapBkrToBkx ? (
                        <Button
                          color='danger'
                          className='mb-2 mb-sm-0 mr-sm-2'
                          onClick={ handleSwapBkrToBkx }
                          disabled={ isSwappingBkrToBkx }
                        >
                          { isSwappingBkrToBkx ? 'Processing...' : 'Mark BKR as BKX' }
                        </Button>
                      ) : null
                    }
                    {
                      canSwapBrvToBkv ? (
                        <Button
                          color='success'
                          className='mb-2 mb-sm-0 mr-sm-2'
                          onClick={ handleSwapBrvToBkv }
                          disabled={ isSwappingBrvToBkv }
                        >
                          { isSwappingBrvToBkv ? 'Swapping...' : 'Swap BRV to BKV' }
                        </Button>
                      ) : null
                    }
                  </div>
                  { canAdjustSellableQuantity &&
                    <div className='border-top mt-3 pt-3'>
                      <div className='font-weight-bold text-muted mb-2'>SQ Editor</div>
                      { isBkc && <p className='small text-muted'>Khusus BKC: Sold tanpa kewajiban Deal mengatur SQ/IQ 0. Unsold mengembalikan SQ/IQ 1.</p> }
                      <div className='d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center text-nowrap' style={{ gap: '0.5rem' }}>
                        <Button color='danger' className='mb-2 mb-sm-0 mr-sm-2' disabled={ isAdjustingSq } onClick={ () => { setSqAdjustment('sold'); setSqReason(''); setSqError('') } }>{ isBkc ? 'Mark Sold (SQ 0 / IQ 0)' : 'Make Sold (SQ 0)' }</Button>
                        <Button color='success' className='mb-2 mb-sm-0 mr-sm-2' disabled={ isAdjustingSq || (!isBkc && Number(inventory.stored_quantity) < 1) } onClick={ () => { setSqAdjustment('in_stock'); setSqReason(''); setSqError('') } }>{ isBkc ? 'Mark Unsold (SQ 1 / IQ 1)' : 'Make In Stock (SQ 1)' }</Button>
                      </div>
                    </div> }
                </CardFooter>
              ) : null
            }
          </Card>
        </Col>
        <Col xs={ 12 } xl={ 5 }>      
          <Card>
            <div className='p-3'>
              <div className='d-flex flex-column flex-md-row align-items-md-center justify-content-between'>
                <div>
                  <h4>{ 'Inventory Movement' }</h4>
                  <CardText className='mb-0'>
                    { 'Sejarah movement dari consignment, live, checkout, shipment, return, internal request, dan adjustment.' }
                  </CardText>
                </div>

                <Button
                  color='primary'
                  outline
                  size='sm'
                  className='mt-3 mt-md-0'
                  onClick={ toggleInventoryMovement }
                >
                  { isInventoryMovementOpen ? 'Hide Movement' : 'Show Movement' }
                </Button>
              </div>
            </div>

            {
              isInventoryMovementOpen ? (
                <CardBody>
                  <InventoryMovementTimelineWithLoader
                    isLoading={ isLoading }
                    inventoryMovements={ inventory.movements }
                    onProcessButtonClick={ handleProcess }
                  />
                </CardBody>
              ) : null
            }
          </Card>
        </Col>
      </Row>

      <SendPhotoLinkEmailModal
        isOpen={ isPhotoLinkEmailOpen }
        product={ product }
        onClose={ () => setIsPhotoLinkEmailOpen(false) }
        onSent={ () => {
          pageUtils.setAlertMsg('Successfully input photo link and send email.', ALERT_TYPES.SUCCESS)
          setReinitializePage(true)
        } }
      />

      <BulkMarkSoldOnHoldModal
        isOpen={ isBulkMarkStatusOpen }
        toggle={ () => setIsBulkMarkStatusOpen(false) }
        defaultSkuList={ inventory.product?.sku || inventory.inventory_sku || '' }
        defaultAction='sold'
        pageUtils={ pageUtils }
        onCompleted={ () => setReinitializePage(true) }
      />

      <Modal isOpen={ sqAdjustment !== null && canAdjustSellableQuantity } toggle={ () => !isAdjustingSq && setSqAdjustment(null) } centered>
        <ModalHeader toggle={ isAdjustingSq ? undefined : () => setSqAdjustment(null) }>{ sqAdjustment === 'sold' ? (isBkc ? 'Mark Sold (SQ 0 / IQ 0)' : 'Make Sold (SQ 0)') : (isBkc ? 'Mark Unsold (SQ 1 / IQ 1)' : 'Make In Stock (SQ 1)') }</ModalHeader>
        <ModalBody>
          <p><strong>{ product.sku }</strong></p>
          <p>SQ: { inventory.sellable_quantity } → { sqAdjustment === 'sold' ? 0 : 1 }. IQ { isBkc ? `${inventory.stored_quantity} → ${sqAdjustment === 'sold' ? 0 : 1}` : `tetap ${inventory.stored_quantity}` }.</p>
          <p className='small text-muted'>{ isBkc ? 'BKC Photo-Only: gunakan Sold jika terjual di seller, atau Unsold untuk membatalkan koreksi.' : sqAdjustment === 'sold' ? 'Make Sold mengikuti Manual Delete dan menonaktifkan produk.' : 'Make In Stock memulihkan status Manual Delete melalui Undo Delete, jika produk masih bisa dipulihkan.' } { isBkc ? 'Khusus BKC: tanpa kewajiban Deal; Sold mengatur SQ/IQ 0, Unsold mengatur SQ/IQ 1 untuk koreksi.' : 'IQ tetap;' } Tidak membuat Deal atau settlement. Reason akan dicatat di Inventory Movement.</p>
          <FormGroup>
            <Label for='sq-adjustment-reason'>Reason (wajib)</Label>
            <Input id='sq-adjustment-reason' type='textarea' value={ sqReason } maxLength={ 2000 } disabled={ isAdjustingSq } onChange={ (event) => setSqReason(event.target.value) } />
          </FormGroup>
          { sqError && <div className='text-danger' role='alert'>{ sqError }</div> }
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' disabled={ isAdjustingSq } onClick={ () => setSqAdjustment(null) }>Cancel</Button>
          <Button color={ sqAdjustment === 'sold' ? 'danger' : 'success' } disabled={ isAdjustingSq || !sqReason.trim() } onClick={ handleAdjustSq }>{ isAdjustingSq ? 'Processing...' : 'Confirm' }</Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={ isMarkStatusOpen } toggle={ () => !isMarkingStatus && setIsMarkStatusOpen(false) } centered>
        <ModalHeader toggle={ isMarkingStatus ? undefined : () => setIsMarkStatusOpen(false) }>
          Mark Product as Sold or On Hold
        </ModalHeader>
        <ModalBody>
          <div className='mb-3'>
            <div><strong>SKU:</strong> { inventory.product?.sku || inventory.inventory_sku || '-' }</div>
            <div><strong>SQ / IQ:</strong> { inventory.sellable_quantity ?? '-' } / { inventory.stored_quantity ?? '-' }</div>
          </div>
          <FormGroup check className='mb-2'>
            <Label check>
              <Input type='radio' name='detail-mark-status-action' value='sold' checked={ markStatusAction === 'sold' } disabled={ isMarkingStatus } onChange={ () => setMarkStatusAction('sold') } />
              {' '}Mark as Sold
            </Label>
            <div className='small text-muted ml-4'>BKC Photo-Only: boleh tanpa Deal jika barang terjual di seller; SQ dan IQ menjadi 0. Selain BKC: wajib termasuk dalam Deal aktif.</div>
          </FormGroup>
          <FormGroup check>
            <Label check>
              <Input type='radio' name='detail-mark-status-action' value='on_hold' checked={ markStatusAction === 'on_hold' } disabled={ isMarkingStatus } onChange={ () => setMarkStatusAction('on_hold') } />
              {' '}Mark as On Hold
            </Label>
            <div className='small text-muted ml-4'>Requires SQ &gt; 0. SQ becomes 0; IQ is unchanged.</div>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' disabled={ isMarkingStatus } onClick={ () => setIsMarkStatusOpen(false) }>Cancel</Button>
          <Button color='primary' disabled={ isMarkingStatus } onClick={ submitMarkSoldOnHold }>
            { isMarkingStatus ? 'Processing...' : 'Submit' }
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

InventoryDetailPage.propTypes = propTypes

export default InventoryDetailPage
